import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';

/**
 * Lexical renders check-list items as `<li role="checkbox" tabindex="-1">`. The
 * tabindex makes the `<li>` focusable, so a mousedown on the item's *text*
 * focuses the `<li>` instead of placing a caret — the click is swallowed and
 * the cursor stays where it was (typically the top of the document after
 * autofocus). Strip the tabindex (keeping role/aria-checked for a11y) so
 * clicking checklist text positions the caret like any other line.
 *
 * Lexical re-adds the attribute whenever it reconciles a check-list item's DOM
 * (e.g. on every edit), and that reconcile does not always surface as a node
 * mutation — so we re-strip after every editor update rather than via a
 * mutation listener. Touching the attribute does not feed back into Lexical, so
 * this does not loop. See T1-6342.
 *
 * Trade-off: a non-focusable `<li>` can no longer be the activeElement, so
 * `registerCheckList`'s keyboard handlers (Space to toggle, arrow-nav between
 * checkbox markers) stop working. Toggling by clicking the marker and normal
 * text-caret navigation are unaffected.
 *
 * Note: `CheckListPlugin`'s newer `disableTakeFocusOnClick` prop does NOT
 * replace this — it only governs clicks inside the marker hit-zone (so tapping
 * the marker doesn't focus the editor, for mobile). The `<li>` still gets
 * `tabIndex="-1"` through at least @lexical 0.45.0, so the text-click
 * focus-steal — and the need for this plugin — persists regardless of version.
 */
export function CheckListCaretFixPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const stripCheckListTabIndex = () => {
      const root = editor.getRootElement();
      root
        ?.querySelectorAll('li[role="checkbox"][tabindex]')
        .forEach((item) => item.removeAttribute('tabindex'));
    };

    // Strip on items already rendered (initial editorState), then after every
    // reconcile that may have re-added the attribute.
    stripCheckListTabIndex();
    return editor.registerUpdateListener(stripCheckListTabIndex);
  }, [editor]);

  return null;
}
